'use client';

import { useState } from 'react';
import { useReveal } from '@/hooks/use-reveal';
import { KeyRound, Clock, Gem, ScrollText, PenLine, Sparkles } from 'lucide-react';

export default function SalonPrive() {
  const { ref, visible } = useReveal();
  const [hoveredPillar, setHoveredPillar] = useState<number | null>(null);

  const pillars = [
    {
      icon: KeyRound,
      kicker: 'Sur Invitation',
      title: 'Une Adresse Discrète',
      text: "Niché dans un passage couvert du 2e arrondissement, notre salon particulier n'est signalé par aucune enseigne. On n'y entre que sur rendez-vous, accueilli personnellement par le maître artisan.",
    },
    {
      icon: PenLine,
      kicker: 'Croquis à la Main',
      title: 'Conception sur Mesure',
      text: "Assis face à l'artisan, vous décrivez la pièce que vous porterez. Croquis au crayon, sélection des cuirs, choix des patines — chaque décision se prend ensemble, sans intermédiaire.",
    },
    {
      icon: Gem,
      kicker: 'Pièce Unique',
      title: 'Numérotée & Signée',
      text: "Votre création porte un numéro qui n'existera qu'une seule fois au monde. Elle est accompagnée d'un certificat calligraphié à l'encre brune, scellé à la cire par l'atelier.",
    },
    {
      icon: ScrollText,
      kicker: 'Suivi à Vie',
      title: 'Entretien & Restaurations',
      text: "La relation avec votre pièce ne s'arrête pas à la livraison. Nous offrons le reconditionnement, la réimperméabilisation, et la restauration complète — à vie, sur présentation du certificat.",
    },
  ];

  const stats = [
    { value: '60h', label: "Heures pour un Manteau" },
    { value: '12', label: "Étapes de Patine" },
    { value: '1/1', label: "Pièce Unique" },
    { value: '∞', label: "Garantie à Vie" },
  ];

  return (
    <section
      id="salon-prive"
      ref={ref}
      className="relative py-32 md:py-44 bg-[#1A0E08] overflow-hidden"
    >
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              'radial-gradient(ellipse at 50% 30%, rgba(201, 169, 97, 0.18) 0%, transparent 55%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(248, 241, 228, 0.6) 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A0E08] via-transparent to-[#1A0E08]" />
      </div>

      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent via-[#C9A961]/60 to-transparent" />
      <div className="absolute top-20 left-1/2 -translate-x-1/2">
        <div className="flex items-center gap-2">
          <div className="w-1 h-1 bg-[#C9A961] rotate-45" />
          <div className="w-2 h-2 border border-[#C9A961]/60 rotate-45" />
          <div className="w-1 h-1 bg-[#C9A961] rotate-45" />
        </div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-24">
          <div className={`reveal ${visible ? 'visible' : ''} mb-8`}>
            <div className="flex items-center justify-center gap-4">
              <div className="w-10 h-px bg-[#C9A961]" />
              <span className="text-[#C9A961] font-serif-display text-sm tracking-[0.4em] uppercase">
                Le Salon Particulier
              </span>
              <div className="w-10 h-px bg-[#C9A961]" />
            </div>
          </div>

          <h2
            className={`reveal ${visible ? 'visible' : ''} font-display text-[#F8F1E4] text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.05] mb-8`}
          >
            Pour Celui qui Cherche
            <br />
            <span className="italic text-luxe-gold">l'Incomparable</span>
          </h2>

          <p
            className={`reveal ${visible ? 'visible' : ''} text-[#F8F1E4]/80 text-lg md:text-xl font-serif-display font-light leading-relaxed mb-6`}
          >
            Au-delà de nos collections, nous réservons à quelques amateurs un cheminement
            d'exception : la création d'une pièce unique, façonnée selon vos mesures, vos
            désirs, et la mémoire que vous souhaitez lui confier. Une relation confidentielle,
            tenue hors du temps et hors du monde.
          </p>

          <div
            className={`reveal ${visible ? 'visible' : ''} inline-flex items-center gap-3 px-5 py-2.5 border border-[#C9A961]/40 bg-[#C9A961]/5 backdrop-blur-sm`}
          >
            <Clock size={14} className="text-[#C9A961]" />
            <span className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-serif-display">
              Uniquement sur Rendez-Vous
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#C9A961]/15 max-w-6xl mx-auto">
          {pillars.map((pillar, idx) => {
            const Icon = pillar.icon;
            const isHovered = hoveredPillar === idx;
            return (
              <div
                key={pillar.title}
                className={`reveal ${visible ? 'visible' : ''} group relative bg-[#1A0E08] p-10 md:p-12 transition-colors duration-700 ${
                  isHovered ? 'bg-[#2B1810]' : ''
                }`}
                style={{ transitionDelay: `${idx * 100}ms` }}
                onMouseEnter={() => setHoveredPillar(idx)}
                onMouseLeave={() => setHoveredPillar(null)}
              >
                <div className="absolute top-6 right-6 font-display text-5xl text-[#C9A961]/10 group-hover:text-[#C9A961]/30 transition-colors duration-700 select-none">
                  {String(idx + 1).padStart(2, '0')}
                </div>

                <div className="relative mb-8">
                  <div className="absolute -inset-3 rounded-full bg-[#C9A961]/0 group-hover:bg-[#C9A961]/10 blur-xl transition-all duration-700" />
                  <Icon
                    size={32}
                    strokeWidth={1.25}
                    className="text-[#C9A961] relative"
                  />
                </div>

                <div className="text-[#C9A961]/70 text-[10px] tracking-[0.35em] uppercase font-serif-display mb-3">
                  {pillar.kicker}
                </div>

                <h3 className="font-display text-2xl text-[#F8F1E4] mb-4 tracking-wide leading-snug">
                  {pillar.title}
                </h3>

                <p className="text-[#F8F1E4]/65 text-base font-serif-display font-light leading-relaxed">
                  {pillar.text}
                </p>

                <div className="mt-8 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="w-8 h-px bg-[#C9A961]/60" />
                  <Sparkles size={10} className="text-[#C9A961]/60" />
                  <div className="w-8 h-px bg-[#C9A961]/60" />
                </div>
              </div>
            );
          })}
        </div>

        <div
          className={`reveal ${visible ? 'visible' : ''} mt-24 max-w-5xl mx-auto`}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#C9A961]/15">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="bg-[#1A0E08] px-6 py-10 text-center"
              >
                <div className="font-display text-4xl md:text-5xl text-luxe-gold mb-3 tracking-wide">
                  {stat.value}
                </div>
                <div className="text-[#F8F1E4]/60 text-[10px] tracking-[0.25em] uppercase font-serif-display leading-relaxed">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className={`reveal ${visible ? 'visible' : ''} mt-28 max-w-3xl mx-auto text-center`}
        >
          <div className="divider-gold w-32 mx-auto mb-10" />
          <blockquote className="font-display text-2xl md:text-3xl text-[#F8F1E4] italic font-light leading-relaxed mb-8">
            « Une pièce d'exception ne s'achète pas.
            <br />
            <span className="text-luxe-gold">Elle se mérite, elle se transmet, elle se vit. »</span>
          </blockquote>

          <a
            href="/salon-prive"
            className="group inline-flex items-center gap-4 px-10 py-5 bg-[#C9A961] text-[#1A0E08] hover:bg-[#F4E4BC] transition-all duration-500"
          >
            <span className="text-xs tracking-[0.3em] uppercase font-serif-display font-bold">
              Composer ma Demande d'Audience
            </span>
            <KeyRound
              size={16}
              className="group-hover:rotate-12 transition-transform duration-500"
            />
          </a>

          <p className="mt-8 text-[#F8F1E4]/40 text-xs font-serif-display italic tracking-wide">
            Réponse sous 48 heures · Sélection discrète des demandes · Confidentialité absolue
          </p>
        </div>
      </div>

      <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
        <div className="flex items-center gap-2 mb-20">
          <div className="w-1 h-1 bg-[#C9A961] rotate-45" />
          <div className="w-2 h-2 border border-[#C9A961]/60 rotate-45" />
          <div className="w-1 h-1 bg-[#C9A961] rotate-45" />
        </div>
      </div>
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-t from-transparent via-[#C9A961]/60 to-transparent" />
    </section>
  );
}
